import { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiDownload, FiUpload, FiFileText, FiCheckCircle } from "react-icons/fi";
import { Sidebar } from "../components/Note";
import api, { API_BASE } from "../api";
import "../styles/tailwind.output.css";

const MAX_SIZE_BYTES = 10 * 1024 * 1024;

function formatBytes(value) {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let idx = 0;
  while (size >= 1024 && idx < units.length - 1) {
    size /= 1024;
    idx += 1;
  }
  return `${size.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

export default function AlimentarBD() {
  const [dark, setDark] = useState(() => {
    if (typeof document === "undefined") return true;
    return document.documentElement.classList.contains("dark");
  });
  const inputRef = useRef(null);

  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const templateUrl = useMemo(() => {
    if (/^https?:\/\//i.test(API_BASE)) {
      return `${API_BASE.replace(/\/$/, "")}/contactos/import-template/`;
    }
    return `${API_BASE}contactos/import-template/`;
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const sync = () => setDark(root.classList.contains("dark"));
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    const onStorage = (event) => {
      if (event.key === "theme") sync();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const clearFeedback = () => {
    setMessage("");
    setError("");
  };

  const validateFile = (file) => {
    if (!file) return "Selecciona un archivo";
    const name = String(file.name || "").toLowerCase();
    const validExt = name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv");
    if (!validExt) return "Formato no válido. Usa .xlsx, .xls o .csv";
    if (Number(file.size || 0) > MAX_SIZE_BYTES) return "El archivo supera el máximo de 10MB";
    return "";
  };

  const setFile = (file) => {
    clearFeedback();
    const validationError = validateFile(file);
    if (validationError) {
      setSelectedFile(null);
      setError(validationError);
      return;
    }
    setSelectedFile(file);
  };

  const onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0] || null;
    setFile(file);
  };

  const onUpload = async () => {
    clearFeedback();
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("create_list", "false");
      formData.append("allow_duplicates", "false");

      const { data } = await api.post("contactos/import/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const created = Number(data?.created || 0);
      const duplicated = Number(data?.duplicate_rows || 0);
      const skipped = Number(data?.skipped || 0);
      setMessage(`Importación completada. Nuevos: ${created}. Duplicados omitidos: ${duplicated}. Omitidos por error: ${skipped}.`);
      setSelectedFile(null);
    } catch (err) {
      setError(String(err?.response?.data?.detail || err?.message || "No se pudo importar el archivo"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`ws-page ws-page-alimentar flex min-h-screen ${dark ? "bg-[#050d1c] text-slate-100" : "bg-[#eef3fb] text-[#12243d]"}`}>
      <Sidebar />

      <main className="flex-1 px-4 py-6 md:px-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header className={`ws-header-surface rounded-2xl border px-6 py-5 ${dark ? "border-[#21324d] bg-[#10192a]" : "border-[#d4deec] bg-white"}`}>
            <h1 className={`text-3xl font-bold tracking-tight ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Importar BD</h1>
            <p className={`mt-1 text-sm ${dark ? "text-[#8ba3c3]" : "text-[#4d688a]"}`}>Carga contactos desde archivos Excel o CSV</p>
          </header>

          <section className={`ws-panel rounded-2xl border p-5 md:p-6 ${dark ? "border-[#25344f] bg-[#111a2b]" : "border-[#d4deec] bg-white"}`}>
            <h2 className={`text-2xl font-semibold ${dark ? "text-[#e4edf9]" : "text-[#14263f]"}`}>Importar contactos desde archivo</h2>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className={`rounded-xl border p-4 ${dark ? "border-[#2a3b57] bg-[#151f31]" : "border-[#d4deec] bg-[#f8fbff]"}`}>
                <div className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-[#061220]">1</div>
                <p className={`text-xl font-semibold ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Descarga la plantilla</p>
                <p className={`mt-1 text-sm ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>Usa nuestra plantilla Excel o CSV para preparar tus contactos.</p>
                <a
                  href={templateUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-lg border border-emerald-500/45 bg-emerald-500/12 px-4 py-2 text-sm font-semibold text-emerald-300 transition hover:border-emerald-400 hover:bg-emerald-500/18"
                >
                  <FiDownload size={14} />
                  Descargar plantilla CSV
                </a>
              </div>

              <div className={`rounded-xl border p-4 ${dark ? "border-[#2a3b57] bg-[#151f31]" : "border-[#d4deec] bg-[#f8fbff]"}`}>
                <div className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-[#061220]">2</div>
                <p className={`text-xl font-semibold ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Completa los datos</p>
                <p className={`mt-1 text-sm ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>Agrega nombres, teléfonos con código de país y etiquetas opcionales.</p>
              </div>

              <div className={`rounded-xl border p-4 ${dark ? "border-[#2a3b57] bg-[#151f31]" : "border-[#d4deec] bg-[#f8fbff]"}`}>
                <div className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-[#061220]">3</div>
                <p className={`text-xl font-semibold ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Sube el archivo</p>
                <p className={`mt-1 text-sm ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>Arrastra o selecciona el archivo para importarlo automáticamente.</p>
              </div>
            </div>
          </section>

          <section
            className={`rounded-2xl border border-dashed p-8 text-center transition ${
              dragActive
                ? "border-sky-400 bg-sky-500/10"
                : dark
                  ? "border-[#2f4566] bg-[#0d1728]"
                  : "border-[#d4deec] bg-white"
            }`}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
            }}
            onDrop={onDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <span className={`mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full ${dark ? "bg-[#1b2a44] text-[#b8cbe8]" : "bg-[#e7eef9] text-[#2f4d72]"}`}>
              <FiUpload size={28} />
            </span>

            <p className={`text-2xl font-semibold ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Arrastra tu archivo aquí o haz clic para seleccionar</p>
            <p className={`mt-2 text-sm ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>Formatos aceptados: .xlsx, .csv, .xls · Máx. 10MB</p>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg border border-[#3a516f] bg-[#17253d] px-4 py-2 text-sm font-semibold text-[#c5d7f0] transition hover:border-[#4b6891] hover:text-white"
              >
                <FiFileText size={14} />
                Seleccionar archivo
              </button>

              <button
                type="button"
                onClick={onUpload}
                disabled={!selectedFile || uploading}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/45 bg-emerald-500/12 px-4 py-2 text-sm font-semibold text-emerald-300 transition hover:border-emerald-400 hover:bg-emerald-500/18 disabled:cursor-not-allowed disabled:opacity-55"
              >
                <FiUpload size={14} />
                {uploading ? "Importando..." : "Importar archivo"}
              </button>
            </div>

            {selectedFile && (
              <p className={`mt-4 text-sm ${dark ? "text-[#c2d5ef]" : "text-[#1f3555]"}`}>
                Archivo seleccionado: <span className="font-semibold">{selectedFile.name}</span> ({formatBytes(selectedFile.size)})
              </p>
            )}
          </section>

          {message && (
            <div className="rounded-xl border border-emerald-500/35 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
              <p className="inline-flex items-center gap-2 font-semibold">
                <FiCheckCircle />
                Éxito
              </p>
              <p className="mt-1">{message}</p>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-rose-500/35 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
              <p className="inline-flex items-center gap-2 font-semibold">
                <FiAlertTriangle />
                Error
              </p>
              <p className="mt-1">{error}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}